/**
 * Ambient audio synthesizer for Simulation Theory.
 * Uses Web Audio API with gentle low-pass filtered sine oscillators.
 * Safe fallback: never throws, stays off by default, fades smoothly, cleans up on unmount.
 */

class AmbientSoundEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private filter: BiquadFilterNode | null = null;
  private oscillators: OscillatorNode[] = [];
  private isPlaying = false;

  public isSupported(): boolean {
    if (typeof window === "undefined") return false;
    return typeof (window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext) !== "undefined";
  }

  public getPlaying(): boolean {
    return this.isPlaying;
  }

  public start(): boolean {
    if (this.isPlaying) return true;
    try {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AudioCtx) return false;

      if (!this.ctx) {
        this.ctx = new AudioCtx();
      }

      if (this.ctx.state === "suspended") {
        this.ctx.resume();
      }

      const now = this.ctx.currentTime;

      // Master output gain with slow fade-in
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.setValueAtTime(0.0001, now);
      // Max volume is very quiet (~0.07) to stay gentle and non-intrusive
      this.masterGain.gain.exponentialRampToValueAtTime(0.07, now + 2.5);
      this.masterGain.connect(this.ctx.destination);

      // Low pass filter around 240Hz for warm deep cosmic tone
      this.filter = this.ctx.createBiquadFilter();
      this.filter.type = "lowpass";
      this.filter.frequency.setValueAtTime(220, now);
      this.filter.connect(this.masterGain);

      // 4 slow gentle sine oscillators (fundamental 55Hz (A1), 110Hz (A2), 165Hz (E3), 220Hz (A3)) with slight detune
      const freqs = [55, 110, 164.8, 220];
      const detunes = [-4, 3, -2, 5];

      this.oscillators = [];
      freqs.forEach((freq, idx) => {
        if (!this.ctx || !this.filter) return;
        const osc = this.ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, now);
        osc.detune.setValueAtTime(detunes[idx] || 0, now);

        const oscGain = this.ctx.createGain();
        oscGain.gain.setValueAtTime(0.25, now);

        osc.connect(oscGain);
        oscGain.connect(this.filter);
        osc.start(now);
        this.oscillators.push(osc);
      });

      this.isPlaying = true;
      return true;
    } catch {
      this.stop();
      return false;
    }
  }

  public stop(): void {
    if (!this.isPlaying && !this.ctx) return;
    try {
      if (this.ctx && this.masterGain) {
        const now = this.ctx.currentTime;
        this.masterGain.gain.cancelScheduledValues(now);
        this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, now);
        this.masterGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.8);

        setTimeout(() => {
          this.cleanupOscillators();
          this.isPlaying = false;
        }, 850);
      } else {
        this.cleanupOscillators();
        this.isPlaying = false;
      }
    } catch {
      this.cleanupOscillators();
      this.isPlaying = false;
    }
  }

  private cleanupOscillators(): void {
    try {
      this.oscillators.forEach((osc) => {
        try {
          osc.stop();
          osc.disconnect();
        } catch {
          // Ignore
        }
      });
      this.oscillators = [];
      if (this.masterGain) {
        this.masterGain.disconnect();
        this.masterGain = null;
      }
      if (this.filter) {
        this.filter.disconnect();
        this.filter = null;
      }
    } catch {
      // Ignore
    }
  }

  public toggle(): boolean {
    if (this.isPlaying) {
      this.stop();
      return false;
    } else {
      return this.start();
    }
  }
}

export const ambientSound = new AmbientSoundEngine();
